import { useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  ShoppingCart,
  Package,
  Users,
  Wrench,
  FileText,
  DollarSign,
  X,
  LucideIcon,
} from 'lucide-react';

interface FABAction {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  color?: string;
}

interface PageConfig {
  permission: string;
  actions: (navigate: (path: string) => void, t: (key: string, options?: Record<string, unknown>) => string) => FABAction[];
}

// Page-specific FAB configurations
const pageConfigs: Record<string, PageConfig> = {
  '/': {
    permission: 'sales_view',
    actions: (navigate, t) => [
      {
        icon: ShoppingCart,
        label: t('navigation:fab.newSale', { defaultValue: 'New Sale' }),
        onClick: () => navigate('/sales?action=new'),
        color: 'bg-green-500 hover:bg-green-600',
      },
    ],
  },
  '/inventory': {
    permission: 'inventory_manage',
    actions: (navigate, t) => [
      {
        icon: Package,
        label: t('navigation:fab.addProduct', { defaultValue: 'Add Product' }),
        onClick: () => navigate('/inventory?action=new'),
        color: 'bg-blue-500 hover:bg-blue-600',
      },
    ],
  },
  '/sales': {
    permission: 'sales_view',
    actions: (navigate, t) => [
      {
        icon: ShoppingCart,
        label: t('navigation:fab.newSale', { defaultValue: 'New Sale' }),
        onClick: () => navigate('/sales?action=new'),
        color: 'bg-green-500 hover:bg-green-600',
      },
    ],
  },
  '/customers': {
    permission: 'customers_manage',
    actions: (navigate, t) => [
      {
        icon: Users,
        label: t('navigation:fab.addCustomer', { defaultValue: 'Add Customer' }),
        onClick: () => navigate('/customers?action=new'),
        color: 'bg-purple-500 hover:bg-purple-600',
      },
    ],
  },
  '/repairs': {
    permission: 'repairs_manage',
    actions: (navigate, t) => [
      {
        icon: Wrench,
        label: t('navigation:fab.newRepair', { defaultValue: 'New Repair' }),
        onClick: () => navigate('/repairs?action=new'),
        color: 'bg-orange-500 hover:bg-orange-600',
      },
    ],
  },
  '/quotes': {
    permission: 'sales_view',
    actions: (navigate, t) => [
      {
        icon: FileText,
        label: t('navigation:fab.newQuote', { defaultValue: 'New Quote' }),
        onClick: () => navigate('/quotes?action=new'),
        color: 'bg-cyan-500 hover:bg-cyan-600',
      },
    ],
  },
  '/expenses': {
    permission: 'expenses_manage',
    actions: (navigate, t) => [
      {
        icon: DollarSign,
        label: t('navigation:fab.addExpense', { defaultValue: 'Add Expense' }),
        onClick: () => navigate('/expenses?action=new'),
        color: 'bg-red-500 hover:bg-red-600',
      },
    ],
  },
};

export default function FloatingActionButton(): ReactNode {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  // Get current page config
  const currentPath = location.pathname;
  const pageConfig = pageConfigs[currentPath];

  // Hide FAB on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsExpanded(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close expanded menu when route changes
  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  // Don't render if no config for this page or no permission
  if (!pageConfig || !hasPermission(pageConfig.permission)) {
    return null;
  }

  const actions = pageConfig.actions(navigate, t);

  // Single action - just show the button
  if (actions.length === 1) {
    const action = actions[0];
    const Icon = action.icon;

    return (
      <button
        onClick={action.onClick}
        className={`fixed right-4 bottom-20 sm:bottom-6 z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-300 ${
          action.color || 'bg-accent-600 hover:bg-accent-700'
        } ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
        aria-label={action.label}
      >
        <Icon size={24} />
      </button>
    );
  }

  // Multiple actions - expandable FAB
  return (
    <div className={`fixed right-4 bottom-20 sm:bottom-6 z-30 transition-all duration-300 ${
      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
    }`}>
      {/* Expanded actions */}
      {isExpanded && (
        <div className="absolute bottom-16 right-0 flex flex-col-reverse gap-3 mb-2">
          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={() => {
                  action.onClick();
                  setIsExpanded(false);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-white transition-all ${
                  action.color || 'bg-accent-600 hover:bg-accent-700'
                }`}
              >
                <Icon size={20} />
                <span className="text-sm font-medium whitespace-nowrap">{action.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main FAB button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-300 ${
          isExpanded ? 'bg-gray-700 rotate-45' : 'bg-accent-600 hover:bg-accent-700'
        }`}
        aria-label={isExpanded ? 'Close menu' : 'Open actions menu'}
        aria-expanded={isExpanded}
      >
        {isExpanded ? <X size={24} /> : <Plus size={24} />}
      </button>

      {/* Backdrop */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-[-1]"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
}
